import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  ShoppingCartIcon,
  ArrowRightOnRectangleIcon,
  ArrowRightEndOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import { useCartStore } from '../../stores/cartStore';
import { authService } from '../../services/authService';
import { useNotifications } from './NotificationContainer';
import NavbarPromoBar from '../nav/NavbarPromoBar';
import NavbarDesktopNav from '../nav/NavbarDesktopNav';
import NavbarMobileMenu from '../nav/NavbarMobileMenu';
import NavbarSearchStrip from '../nav/NavbarSearchStrip';
import { BRAND_NAME, BRAND_TAGLINE } from '../nav/navData';
import { useNavCategories } from '../../hooks/useNavCategories';

const Navbar: React.FC = () => {
  const { isAuthenticated, logout, user } = useAuthStore();
  const { getItemCount } = useCartStore();
  const { showError } = useNotifications();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCategories, setShowCategories] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const navCategories = useNavCategories();
  const categoryLinks = useMemo(
    () => navCategories.map((c) => ({ href: `/productos?categoria=${c._id}`, label: c.nombre })),
    [navCategories],
  );
  const categoryMenuItems = useMemo(
    () =>
      categoryLinks.length > 0 ? categoryLinks : [{ href: '/productos', label: 'Ver catálogo completo' }],
    [categoryLinks],
  );

  const handleLogout = async () => {
    try {
      await authService.logout();
      logout();
      navigate('/');
    } catch {
      showError('Sesión', 'No se pudo cerrar sesión en el servidor; saliste localmente.');
      logout();
      navigate('/');
    }
  };

  const toggleSearch = () => {
    const next = !showSearch;
    setShowSearch(next);
    if (next) {
      window.setTimeout(() => {
        document.getElementById('navbar-search-input')?.focus();
      }, 50);
    }
  };

  const iconBtn =
    'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/10 text-white transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-400/80';

  return (
    <>
      <NavbarPromoBar />
      <header className="sticky top-0 z-nav overflow-visible border-b border-white/[0.08] bg-accent-950 text-white shadow-sm">
        <div className="mx-auto flex h-[4.75rem] w-full max-w-[1600px] items-center gap-5 px-4 sm:px-6 lg:h-[5.25rem] lg:gap-8 lg:px-8 xl:gap-10 xl:px-10">
          {/* Marca */}
          <Link to="/" className="flex shrink-0 items-center gap-3">
            <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white p-1 shadow-sm lg:h-[3.75rem] lg:w-[3.75rem]">
              <img
                src="/images/Logo.png"
                alt=""
                className="h-full w-full scale-110 object-contain"
                aria-hidden
              />
            </span>
            <span className="hidden sm:flex sm:flex-col">
              <span className="font-display text-base font-semibold leading-none text-white lg:text-lg">
                {BRAND_NAME}
              </span>
              <span className="mt-1 hidden text-[10px] leading-snug text-primary-100/85 2xl:inline 2xl:max-w-[14rem]">
                {BRAND_TAGLINE}
              </span>
            </span>
          </Link>

          {/* Menú — una sola línea, nunca wrap */}
          <nav className="hidden min-w-0 flex-1 items-center justify-center overflow-x-auto lg:flex">
            <NavbarDesktopNav
              categoriesOpen={showCategories}
              onToggleCategories={() => setShowCategories((v) => !v)}
              categoryLinks={categoryMenuItems}
            />
          </nav>

          {/* Acciones */}
          <div className="ml-auto flex shrink-0 items-center gap-2 border-l border-white/15 pl-4 sm:gap-2.5 sm:pl-5 lg:ml-0 lg:pl-6">
            <button type="button" onClick={toggleSearch} className={iconBtn} aria-label="Abrir búsqueda">
              <MagnifyingGlassIcon className="h-5 w-5" />
            </button>

            {isAuthenticated ? (
              <>
                <Link
                  to="/profile"
                  className="hidden items-center gap-2 rounded-lg px-2 py-1.5 text-white/90 transition-colors hover:bg-white/10 xl:inline-flex"
                >
                  <UserIcon className="h-5 w-5 shrink-0" aria-hidden />
                  <span className="max-w-[7rem] truncate text-sm font-medium">
                    {user?.nombre || 'Mi cuenta'}
                  </span>
                </Link>
                <Link
                  to="/carrito"
                  className="relative rounded-lg p-2 text-white/90 transition-colors hover:bg-white/10"
                  aria-label={`Carrito, ${getItemCount()} artículos`}
                >
                  <ShoppingCartIcon className="h-5 w-5" />
                  {getItemCount() > 0 ? (
                    <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-secondary-500 px-1 text-[10px] font-bold text-white">
                      {getItemCount() > 99 ? '99+' : getItemCount()}
                    </span>
                  ) : null}
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="hidden items-center gap-2 rounded-lg px-2 py-1.5 text-white/90 transition-colors hover:bg-white/10 xl:inline-flex"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0" aria-hidden />
                  <span className="text-sm font-medium">Salir</span>
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-white/90 transition-colors hover:bg-white/10 lg:inline-flex"
                >
                  <ArrowRightEndOnRectangleIcon className="h-5 w-5 shrink-0" aria-hidden />
                  Iniciar sesión
                </Link>
                <Link
                  to="/register"
                  className="hidden rounded-lg bg-secondary-400 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-secondary-500 lg:inline-block"
                >
                  Registrarse
                </Link>
              </>
            )}

            <button
              type="button"
              className={`${iconBtn} lg:hidden`}
              onClick={() => setShowMobileMenu((v) => !v)}
              aria-expanded={showMobileMenu}
              aria-label={showMobileMenu ? 'Cerrar menú' : 'Abrir menú'}
            >
              {showMobileMenu ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <NavbarSearchStrip
          open={showSearch}
          query={searchQuery}
          onQueryChange={setSearchQuery}
          onClose={() => setShowSearch(false)}
        />

        <NavbarMobileMenu
          open={showMobileMenu}
          onClose={() => setShowMobileMenu(false)}
          isAuthenticated={isAuthenticated}
          itemCount={getItemCount()}
          onLogout={handleLogout}
          categoryLinks={categoryMenuItems}
        />
      </header>
    </>
  );
};

export default Navbar;
